import client from 'braintree-web/client';
import applePay from 'braintree-web/apple-pay';
import dataCollector from 'braintree-web/data-collector';
import {
  notifyApmAvailability,
  createButtonStateObserver,
  getCountryName,
} from './apm-utils';
import { t } from '../utils/i18n';

const initApplePay = async function () {
  if (!window.ApplePaySession) {
    notifyApmAvailability('applePay', false);
    return;
  }

  if (!ApplePaySession.canMakePayments()) {
    notifyApmAvailability('applePay', false);
    return;
  }

  const applePayConfigElement = document.getElementById('apple-pay-config');
  if (!applePayConfigElement) {
    notifyApmAvailability('applePay', false);
    return;
  }

  const applePayConfig = JSON.parse(applePayConfigElement.innerHTML);
  const totalAmount = applePayConfig.totalAmount;
  const displayName = applePayConfig.displayName;
  const clientTokenUrl = applePayConfig.clientTokenUrl;

  const applePayButton = document.getElementById('apple-pay-button');
  const form = document.getElementById('apple-pay-form');
  const paymentMethodNonceField = document.getElementById(
    'apple_pay_payment_method_nonce',
  );
  const deviceDataField = document.getElementById('apple_pay_device_data');
  const billingAddressField = document.getElementById(
    'apple_pay_billing_address',
  );

  if (applePayButton && form) {
    form.style.display = 'inline-block';

    const skeletons = document.getElementById('payment-button-skeletons');
    if (skeletons) {
      skeletons.style.display = 'none';
    }

    notifyApmAvailability('applePay', true);
    createButtonStateObserver(applePayButton);
  }

  // Fetch client token only when Apple Pay is available
  let clientToken;
  try {
    const response = await fetch(clientTokenUrl);
    const data = await response.json();
    clientToken = data.clientToken;
  } catch (error) {
    console.error('Error fetching client token:', error);
    return;
  }

  client.create(
    {
      authorization: clientToken,
    },
    function (clientErr, clientInstance) {
      if (clientErr) {
        console.error('Error creating Braintree client:', clientErr);
        return;
      }

      let deviceData = '';
      dataCollector.create(
        {
          client: clientInstance,
        },
        function (dataCollectorErr, dataCollectorInstance) {
          if (dataCollectorErr) {
            console.error('Error creating data collector:', dataCollectorErr);
            return;
          }

          deviceData = dataCollectorInstance.deviceData;
        },
      );

      applePay.create(
        {
          client: clientInstance,
        },
        function (applePayErr, applePayInstance) {
          if (applePayErr) {
            console.error('Error creating Apple Pay instance:', applePayErr);
            return;
          }

          applePayButton.addEventListener('keydown', function (event) {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              applePayButton.click();
            }
          });

          applePayButton.addEventListener('click', function (event) {
            event.preventDefault();

            // Don't proceed if button is disabled
            if (applePayButton.hasAttribute('disabled')) {
              return;
            }

            const termsCheckbox = document.getElementById(
              'cart-terms-checkbox',
            );
            if (termsCheckbox && !termsCheckbox.checked) {
              alert(t('js.billing.accept_terms'));
              return;
            }

            applePayButton.style.opacity = '0.6';
            applePayButton.style.pointerEvents = 'none';

            const paymentRequest = applePayInstance.createPaymentRequest({
              total: {
                label: displayName,
                amount: totalAmount,
              },
              requiredBillingContactFields: ['postalAddress', 'name'],
              requiredShippingContactFields: ['phone', 'email'],
            });

            const session = new ApplePaySession(3, paymentRequest);

            session.onvalidatemerchant = function (event) {
              applePayInstance.performValidation(
                {
                  validationURL: event.validationURL,
                  displayName: displayName,
                },
                function (validationErr, merchantSession) {
                  if (validationErr) {
                    console.error('Error validating merchant:', validationErr);
                    session.abort();
                    resetButton();
                    return;
                  }

                  session.completeMerchantValidation(merchantSession);
                },
              );
            };

            session.onpaymentauthorized = function (event) {
              applePayInstance.tokenize(
                {
                  token: event.payment.token,
                },
                function (tokenizeErr, payload) {
                  if (tokenizeErr) {
                    console.error('Error tokenizing Apple Pay:', tokenizeErr);
                    session.completePayment(ApplePaySession.STATUS_FAILURE);
                    resetButton();
                    return;
                  }

                  const billingContact = event.payment.billingContact;
                  const shippingContact = event.payment.shippingContact;
                  const countryCode = billingContact.countryCode || 'US';
                  const countryName = getCountryName(countryCode, 'US');

                  const billingAddress = {
                    givenName: billingContact.givenName || '',
                    familyName: billingContact.familyName || '',
                    phoneNumber: shippingContact?.phoneNumber || '',
                    emailAddress: shippingContact?.emailAddress || '',
                    addressLines: billingContact.addressLines || [],
                    locality: billingContact.locality || '',
                    administrativeArea: billingContact.administrativeArea || '',
                    postalCode: billingContact.postalCode || '',
                    countryCode: countryCode,
                    countryName: countryName,
                  };

                  paymentMethodNonceField.value = payload.nonce;
                  deviceDataField.value = deviceData;
                  billingAddressField.value = JSON.stringify(billingAddress);

                  session.completePayment(ApplePaySession.STATUS_SUCCESS);

                  form.submit();
                },
              );
            };

            session.oncancel = function () {
              resetButton();
            };

            session.begin();
          });

          function resetButton() {
            applePayButton.style.opacity = '1';
            applePayButton.style.pointerEvents = 'auto';
          }
        },
      );
    },
  );
};

document.addEventListener('readystatechange', function () {
  if (document.readyState === 'complete') {
    initApplePay();
  }
});

export default initApplePay;
